/*
 * Boston Proper mega-menu hover controller.
 *
 * Replaces Horizon's pointerenter/pointerleave behavior on our
 * `_bp-menu-item` blocks with a debounced version. Horizon's default
 * fires close immediately on pointerleave, which disappears the
 * dropdown before the cursor can travel from the trigger LI down to
 * the submenu content. This script gives the cursor a 200ms grace
 * period to enter the submenu before closing.
 *
 * Open/close state is tracked with a `.bp-menu-open` class on the LI;
 * the actual visibility is handled by CSS (snippets/bp-fonts.liquid).
 *
 * Also handles keyboard focus: focusing the trigger or anything inside
 * the submenu opens it; focus moving fully out closes it.
 */
(function () {
  'use strict';

  const CLOSE_DELAY_MS = 200;
  const wired = new WeakSet();

  // Tracks the currently open item so sibling items can be closed
  // immediately (no delay) when a new one opens, preventing two
  // submenus from overlapping during the 200ms grace period.
  let currentOpen = null;

  function init(scope) {
    const items = (scope || document).querySelectorAll('[data-bp-menu-item]');
    items.forEach((li) => {
      if (wired.has(li)) return;
      wired.add(li);

      let closeTimer = null;
      const submenu = li.querySelector(':scope > .bp-mega-submenu');
      const link = li.querySelector(':scope > .menu-list__link');

      // The mega is hidden with opacity:0 (not display:none), so its content
      // stays in the tab order + focusable when closed — Tab landed inside the
      // dropdown (and focusing it re-opened the menu). `inert` takes the WHOLE
      // subtree out of tab order + focus + pointer while closed, regardless of
      // element type or lazily-added content — belt-and-suspenders over the
      // per-element tabindex=-1. Lifted only while the menu is open (below), so
      // arrows can move through it and clicks work. (QA: Alfred.)
      if (submenu) submenu.setAttribute('inert', '');
      const setInert = (on) => {
        if (!submenu) return;
        if (on) submenu.setAttribute('inert', '');
        else submenu.removeAttribute('inert');
      };

      function cancelClose() {
        if (closeTimer !== null) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
      }

      function closeNow() {
        cancelClose();
        li.classList.remove('bp-menu-open');
        if (link) link.setAttribute('aria-expanded', 'false');
        setInert(true);
        if (currentOpen === li) currentOpen = null;
      }

      function open() {
        // Immediately close whichever other item is open so its submenu
        // doesn't linger behind the one we're about to show.
        if (currentOpen && currentOpen !== li) {
          currentOpen._bpCloseNow();
        }
        cancelClose();
        li.classList.add('bp-menu-open');
        if (link) link.setAttribute('aria-expanded', 'true');
        setInert(false);
        currentOpen = li;
      }

      // Expose open/closeNow so sibling items and the keyboard controller
      // (initKeyboard) can drive the same open/close state (which handles
      // aria-expanded + closing whichever other menu is open).
      li._bpCloseNow = closeNow;
      li._bpOpen = open;

      function scheduleClose() {
        cancelClose();
        closeTimer = setTimeout(() => {
          li.classList.remove('bp-menu-open');
          if (link) link.setAttribute('aria-expanded', 'false');
          setInert(true);
          if (currentOpen === li) currentOpen = null;
          closeTimer = null;
        }, CLOSE_DELAY_MS);
      }

      // LI hover. Per the spec pointerenter on the <li> fires when the
      // cursor crosses into ANY descendant (including the <a> text),
      // so this single binding should be enough. In practice some
      // live-store CSS (app embeds / vendor scripts setting
      // `pointer-events: none` on the link, or an overlay over the
      // text area) can swallow pointerenter just over the link text
      // while leaving the LI's padding/safety-zone hover working —
      // symptom: dropdown opens when you hover the sides/below the
      // text but NOT the text itself. Belt + suspenders: also bind
      // on the link + its inner title span so the open handler still
      // fires regardless of which element actually catches the event.
      li.addEventListener('pointerenter', open);
      li.addEventListener('pointerleave', scheduleClose);
      if (link) {
        link.addEventListener('pointerenter', open);
        // pointerover bubbles + fires on every descendant; use it as
        // the final safety net for the title span / any wrapper.
        link.addEventListener('pointerover', open);
      }

      // Submenu hover — entering it cancels the pending close;
      // leaving the submenu starts the close timer.
      if (submenu) {
        submenu.addEventListener('pointerenter', cancelClose);
        submenu.addEventListener('pointerleave', scheduleClose);
      }

      // Keyboard focus. Menubar pattern (initKeyboard): focusing the top
      // TRIGGER must NOT auto-open — Enter / Space / ArrowDown opens it. Only
      // keep the menu open while focus is INSIDE the submenu (after it's been
      // opened and focus moved in), so a hover-open menu doesn't collapse when
      // focus lands in it. Close when focus leaves the whole item.
      li.addEventListener('focusin', (e) => {
        if (submenu && submenu.contains(e.target)) open();
      });
      li.addEventListener('focusout', (e) => {
        // focusout on LI fires when focus moves to a non-descendant.
        // Use relatedTarget to confirm the new focus isn't inside.
        if (!li.contains(e.relatedTarget)) {
          scheduleClose();
        }
      });

      // Esc closes
      li.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          li.classList.remove('bp-menu-open');
          setInert(true);
          if (link) {
            link.setAttribute('aria-expanded', 'false');
            link.focus();
          }
        }
      });
    });
  }

  // Tabs layout — wire each [data-bp-mega-tabs] container so hovering
  // or clicking a tab activates its panel. Independent of the hover-
  // open/close logic above (which acts on the parent LI).
  const wiredTabs = new WeakSet();

  function initTabs(scope) {
    const containers = (scope || document).querySelectorAll('[data-bp-mega-tabs]');
    containers.forEach((container) => {
      if (wiredTabs.has(container)) return;
      wiredTabs.add(container);

      const tabs = container.querySelectorAll('[data-bp-mega-tab]');
      const panels = container.querySelectorAll('[data-bp-mega-panel]');

      function activate(idx) {
        tabs.forEach((t, i) => {
          const active = i === idx;
          t.classList.toggle('is-active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        panels.forEach((p, i) => {
          const active = i === idx;
          p.classList.toggle('is-active', active);
          if (active) {
            p.removeAttribute('hidden');
          } else {
            p.setAttribute('hidden', '');
          }
        });
      }

      tabs.forEach((tab, i) => {
        tab.addEventListener('mouseenter', () => activate(i));
        tab.addEventListener('focus', () => activate(i));
        tab.addEventListener('click', (e) => {
          e.preventDefault();
          activate(i);
        });
      });
    });
  }

  // "More" button overflow panel — re-activate the panel when the cursor enters
  // the shadow-DOM overflow area or any slotted overflow item so the panel stays
  // open during the transition from the trigger LI to the dropdown content.
  // Horizon's immediate deactivation fires before the cursor reaches the panel;
  // listening on the destination re-opens it the moment the cursor arrives.
  const wiredMore = new WeakSet();

  function initMoreButton(scope) {
    (scope || document).querySelectorAll('header-menu overflow-list').forEach((overflowList) => {
      if (wiredMore.has(overflowList)) return;

      const moreBtn = overflowList.querySelector(':scope > [slot="more"]');
      if (!moreBtn) return;

      const headerMenu = overflowList.closest('header-menu');
      if (!headerMenu) return;

      const shadow = overflowList.shadowRoot;
      if (!shadow) return;

      const overflowPanelEl = shadow.querySelector('[part="overflow-list"]');
      if (!overflowPanelEl) return;

      wiredMore.add(overflowList);

      function ensureActive() {
        headerMenu.cancelOverflowClose?.();
        if (headerMenu.dataset.overflowExpanded !== 'true') {
          headerMenu.activate({ target: moreBtn, relatedTarget: null, type: 'pointerenter' });
        }
      }

      // Re-activate when cursor enters the overflow panel (shadow DOM boundary).
      overflowPanelEl.addEventListener('pointerenter', ensureActive);

      // Re-activate when cursor enters any slotted overflow item (light DOM).
      function wireSlottedItems() {
        overflowList.querySelectorAll(':scope > [slot="overflow"]').forEach((item) => {
          if (item._bpMoreWired) return;
          item._bpMoreWired = true;
          item.addEventListener('pointerenter', ensureActive);

          // Items are moved into the overflow slot AFTER initKeyboard ran, so
          // give this relocated item the FULL keyboard treatment now — not just
          // tabindex suppression, but also `inert` on its dropdown and the
          // trigger's Tab handler — so it behaves exactly like the visible
          // categories. Without this, tabbing FROM Brands We Love (the overflow
          // item) entered its own panel instead of moving on to Search. QA
          // (Alfred). initKeyboard is idempotent (wiredKb guard).
          if (item.matches('[data-bp-menu-item]')) initKeyboard(item);

          // Also cancel close when cursor enters a mega submenu nested inside
          // an overflow item (these are position:fixed, outside overflow panel bounds).
          item.querySelectorAll('.bp-mega-submenu').forEach((submenu) => {
            if (submenu._bpMoreSubmenuWired) return;
            submenu._bpMoreSubmenuWired = true;
            submenu.addEventListener('pointerenter', () => {
              headerMenu.cancelOverflowClose?.();
            });
          });
        });
      }

      wireSlottedItems();
      // overflow-list.js moves items into slot="overflow" lazily; observe for additions.
      new MutationObserver(wireSlottedItems).observe(overflowList, { childList: true });
    });
  }

  // ---- Menubar keyboard navigation (W3C APG menubar; Atlas parity) ----
  // Ticket (Alfred, 7/27): make the desktop nav behave like a menubar — Tab
  // moves between TOP-level items only (not through every sub-link), Enter /
  // Space / ArrowDown opens a submenu and moves focus to its first link,
  // Up/Down arrow-navigate the sub-links, Escape / Tab close and return focus
  // to the top item. Ported from Atlas (src/scripts/sections/header.js). This
  // is a progressive enhancement layered on the hover controller above (which
  // owns the .bp-menu-open / aria-expanded state via li._bpOpen / _bpCloseNow);
  // with JS off, the native tab order still reaches every item (the current,
  // already-accessible behavior).
  const wiredKb = new WeakSet();

  // All top-level triggers, in DOM order — the menubar items. Recomputed per
  // call so it reflects any morphed/added items.
  function triggerList() {
    // The VISIBLE desktop menubar triggers only. Exclude the mobile menu-list
    // (a separate drawer copy of the same categories — initKeyboard skips wiring
    // it, but it must also be excluded here or Tab walks that second copy and
    // the whole menu is tabbed through twice, ~18 stops logo→search) and any
    // hidden item (offsetParent null → display:none, e.g. an item tucked in a
    // closed "More" overflow panel).
    return Array.from(document.querySelectorAll('[data-bp-menu-item] > .menu-list__link'))
      .filter((l) => !l.closest('.menu-list--mobile') && l.offsetParent !== null);
  }

  // Focusable sub-items in an open submenu — links + tab-panel switch buttons
  // — skipping any inside a hidden ([hidden]) tab panel so arrow order matches
  // what's visible. Focusing a tab button activates its panel (initTabs), so
  // arrowing onto it reveals that panel's links next.
  function subLinks(submenu) {
    return Array.from(
      submenu.querySelectorAll('a[href], [data-bp-mega-tab]')
    ).filter((el) => !el.closest('[hidden]'));
  }

  // Take every focusable inside a menu item OUT of the tab order (except the
  // top trigger). Shared by initKeyboard + the overflow-list wiring (overflow
  // items are relocated lazily, after initKeyboard runs, so their dropdown
  // focusables must be re-suppressed on relocation). Idempotent.
  function suppressTabbables(li, trigger) {
    const t = trigger || li.querySelector(':scope > .menu-list__link');
    li.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')
      .forEach((el) => {
        if (el === t) return;
        el.setAttribute('tabindex', '-1');
        if (el.matches('a[href]') && !el.getAttribute('role')) el.setAttribute('role', 'menuitem');
      });
  }

  function initKeyboard(scope) {
    const root = scope || document;
    // Include the scope element itself when it IS a menu item — the overflow
    // path calls initKeyboard(item) on a single relocated <li> (Brands We Love),
    // which querySelectorAll (descendants only) wouldn't otherwise cover.
    const items = new Set(root.querySelectorAll ? root.querySelectorAll('[data-bp-menu-item]') : []);
    if (root.nodeType === 1 && root.matches && root.matches('[data-bp-menu-item]')) items.add(root);
    items.forEach((li) => {
      if (wiredKb.has(li)) return;
      // Desktop menubar only — the mobile menu is a drawer/accordion with its
      // own pattern; leave its native tab order alone.
      if (li.closest('.menu-list--mobile')) { wiredKb.add(li); return; }
      const trigger = li.querySelector(':scope > .menu-list__link');
      if (!trigger) return;
      wiredKb.add(li);
      const submenu = li.querySelector(':scope > .bp-mega-submenu');

      // Menubar semantics + take the sub-links out of the tab order so Tab
      // steps between top items only. Done in JS (not Liquid) so it covers
      // every submenu layout (tiles / columns / promos / tabs) and degrades
      // to the native tab order when JS is off. role="menubar" on the parent
      // list makes the menuitem roles valid ARIA.
      const bar = li.closest('.menu-list__list');
      if (bar) bar.setAttribute('role', 'menubar');
      trigger.setAttribute('role', 'menuitem');
      if (submenu) submenu.setAttribute('role', 'menu');
      // Take EVERY focusable in the dropdown out of the tab order — scoped to
      // the whole <li> (minus the top trigger), NOT just .bp-mega-submenu. The
      // mega is hidden with opacity:0 (NOT display:none), so its content stays
      // tabbable when closed; anything living OUTSIDE .bp-mega-submenu (a stray
      // promo/heading link — one per menu — plus Brands We Love's tab list +
      // panel links + 2 promos) previously stayed in the tab order, adding 14
      // phantom stops (QA: 23 tabs logo→search instead of 9). All remain
      // reachable via arrows once the menu is open. (QA 7/27, Alfred.)
      suppressTabbables(li, trigger);

      const focusTriggerWrap = (delta) => {
        const list = triggerList();
        const i = list.indexOf(trigger);
        if (i === -1 || !list.length) return;
        const t = list[(i + delta + list.length) % list.length];
        if (t) t.focus();
      };
      const focusTriggerNoWrap = (delta) => {
        const list = triggerList();
        const i = list.indexOf(trigger);
        const t = list[i + delta];
        if (t) t.focus();
        return !!t;
      };
      const closeToTrigger = (refocus) => {
        li._bpCloseNow ? li._bpCloseNow() : li.classList.remove('bp-menu-open');
        trigger.setAttribute('aria-expanded', 'false');
        if (refocus) trigger.focus();
      };

      trigger.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'Enter':
          case ' ':
          case 'ArrowDown':
            if (submenu) {
              e.preventDefault();
              if (li._bpOpen) li._bpOpen();
              else { li.classList.add('bp-menu-open'); trigger.setAttribute('aria-expanded', 'true'); }
              const links = subLinks(submenu);
              if (links[0]) links[0].focus();
            }
            break;
          case 'ArrowRight': e.preventDefault(); focusTriggerWrap(1); break;
          case 'ArrowLeft':  e.preventDefault(); focusTriggerWrap(-1); break;
          case 'Escape':     closeToTrigger(false); break;
          case 'Tab': {
            // Tab moves strictly between top-level items — one stop per
            // category, never INTO a dropdown (Arrow Down opens it). This is
            // belt-and-suspenders over inert/tabindex: the trigger owns focus,
            // so its own Tab handler fires before focus can reach any still-
            // tabbable dropdown link — so it works even if inert isn't applied
            // (stale-deploy safety). At the ends (no adjacent trigger) DON'T
            // preventDefault — let native Tab leave the menubar to the next
            // section (search / logo) instead of trapping focus. closeToTrigger
            // always runs (even when there's no next trigger) so a hover-opened
            // last category doesn't linger open while focus moves to the icons —
            // previously this only ran inside `if (moved)`, so the last item's
            // panel relied on a 200ms focusout fallback to close, which read as
            // "the last category's menu opened" instead of Tab moving on.
            const moved = focusTriggerNoWrap(e.shiftKey ? -1 : 1);
            closeToTrigger(false);
            if (moved) e.preventDefault();
            break;
          }
        }
      });

      if (submenu) {
        submenu.addEventListener('keydown', (e) => {
          const link = e.target.closest('a[href], [data-bp-mega-tab]');
          if (!link) return;
          const links = subLinks(submenu);
          const i = links.indexOf(link);
          switch (e.key) {
            case 'ArrowDown':
              e.preventDefault();
              (links[(i + 1) % links.length] || links[0]).focus();
              break;
            case 'ArrowUp':
              e.preventDefault();
              (links[i <= 0 ? links.length - 1 : i - 1] || links[0]).focus();
              break;
            case 'Home': e.preventDefault(); if (links[0]) links[0].focus(); break;
            case 'End':  e.preventDefault(); if (links[links.length - 1]) links[links.length - 1].focus(); break;
            case 'Escape': e.preventDefault(); closeToTrigger(true); break;
            case 'Tab': {
              // Keep Tab on the top level: close and jump to the adjacent top
              // item. At the ends (no adjacent trigger) DON'T preventDefault —
              // let native Tab leave the menu to the next section, instead of
              // trapping focus (QA 7/27, Alfred).
              const moved = focusTriggerNoWrap(e.shiftKey ? -1 : 1);
              closeToTrigger(false);
              if (moved) e.preventDefault();
              break;
            }
          }
        });
      }
    });
  }

  // Public API for sections that morph DOM (theme editor, etc.)
  window.bpMegaHover = {
    init: (scope) => {
      init(scope);
      initTabs(scope);
      initMoreButton(scope);
      initKeyboard(scope);
    }
  };

  // Lock each desktop nav link's min-width to its bold measurement so
  // siblings don't shift when the hovered item transitions to wght 700.
  // Must run after document.fonts.ready — measuring before the variable
  // font loads would give the fallback font's (narrower) width instead.
  function lockNavWidths() {
    document.fonts.ready.then(function () {
      document.querySelectorAll('.menu-list__list-item .menu-list__link').forEach(function (link) {
        link.style.fontVariationSettings = "'wght' 700";
        link.style.fontWeight = '700';
        var w = link.offsetWidth; // synchronous reflow — reads bold width
        link.style.fontVariationSettings = '';
        link.style.fontWeight = '';
        link.style.minWidth = w + 'px';
      });
    });
  }

  function bootstrap() {
    lockNavWidths();
    init();
    initTabs();
    initMoreButton();
    initKeyboard();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
})();
